/**
 * Click Analytics Hook
 *
 * React Query hook for fetching click event analytics from the admin API.
 * Wraps GET /api/v1/admin/analytics/clicks with typed params and response.
 */

import { useQuery } from "@tanstack/react-query";

import { apiRequest } from "../api/client";

// ============================================================================
// Types
// ============================================================================

/** GroupBy dimension for analytics aggregation */
export type ClickGroupBy = "day" | "provider" | "vertical";

/** Query parameters for the click analytics endpoint */
export interface ClickAnalyticsParams {
  readonly startDate: string;
  readonly endDate: string;
  readonly groupBy?: ClickGroupBy;
  readonly verticalKey?: string;
  readonly providerId?: string;
}

/** Single aggregated click summary item from the API */
export interface ClickSummaryItem {
  readonly bucket: string;
  readonly clickCount: number;
  readonly providerId?: string | null;
  readonly verticalKey?: string;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Fetch click analytics from the admin API.
 *
 * Uses React Query for caching, refetching, and loading states.
 * Requires admin:audit capability (enforced server-side).
 *
 * Note: apiRequest unwraps the API envelope, so we receive the inner `data`
 * array directly (ClickSummaryItem[]). The `meta` field from the API response
 * is not available after unwrapping — KPIs are computed client-side instead.
 */
export function useClickAnalytics(
  params: ClickAnalyticsParams,
  options?: { enabled?: boolean; refetchInterval?: number | false }
): {
  items: readonly ClickSummaryItem[];
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  refetch: () => Promise<unknown>;
} {
  const searchParams = new URLSearchParams();
  searchParams.set("startDate", params.startDate);
  searchParams.set("endDate", params.endDate);
  if (params.groupBy !== undefined) searchParams.set("groupBy", params.groupBy);
  if (params.verticalKey !== undefined) searchParams.set("verticalKey", params.verticalKey);
  if (params.providerId !== undefined) searchParams.set("providerId", params.providerId);

  const queryString = searchParams.toString();

  // Resolve refetchInterval: exactOptionalPropertyTypes forbids passing undefined
  const refetchInterval = options?.refetchInterval ?? false;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["click-analytics", queryString],
    queryFn: async (): Promise<readonly ClickSummaryItem[]> => {
      const response = await apiRequest<readonly ClickSummaryItem[]>(
        `/api/v1/admin/analytics/clicks?${queryString}`
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }

      // apiRequest unwraps the envelope { data: [...], meta: {...} } to just [...]
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: options?.enabled !== false,
    refetchInterval,
  });

  return {
    items: data ?? [],
    isLoading,
    isError,
    error: error instanceof Error ? error.message : null,
    refetch,
  };
}

// ============================================================================
// Date Range Helpers
// ============================================================================

/** Predefined date range presets */
export type DateRangePreset = "7d" | "30d" | "90d";

/** Get ISO date strings for a preset range ending now */
export function getDateRange(preset: DateRangePreset): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const end = now.toISOString();

  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  return {
    startDate: start.toISOString(),
    endDate: end,
  };
}
